import React, { useState, useRef, useEffect } from 'react';
import { useInternalChat } from '../../context/InternalChatContext';

function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function shortName(senderId) {
    if (!senderId) return '';
    if (senderId === 'AI Assistant') return 'AI Assistant';
    // show username part before '@' if it's an email
    return senderId.includes('@') ? senderId.split('@')[0] : senderId;
}

export default function TeamChatThread({ session }) {
    const { user, messages, messagesHasMore, loadEarlierMessages, sendMessage, resolveSession, closeThread } = useInternalChat();
    const [input, setInput] = useState('');
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const sessionMessages = messages[session.id] || [];
    const isResolved = session.status === 'RESOLVED';
    const directedTo = session.directedTo;
    const directedShort = directedTo ? (directedTo.includes('@') ? directedTo.split('@')[0] : directedTo) : null;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [sessionMessages.length]);

    const handleSend = () => {
        const trimmed = input.trim();
        if (!trimmed) return;
        sendMessage(session.id, trimmed);
        setInput('');
        textareaRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Show waiting indicator when there's only 1 message (the original question) and session is open
    const showWaiting = !isResolved && sessionMessages.length === 1;

    return (
        <div className="tc-thread">
            <div className="tc-thread-header">
                <button className="tc-back-btn" onClick={closeThread}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <span className="tc-thread-title">{session.title}</span>
                {directedShort && (
                    <span className="tc-directed-tag-header">→ @{directedShort}</span>
                )}
                {!isResolved && (
                    <button className="tc-resolve-btn" onClick={() => resolveSession(session.id)}>
                        Resolve
                    </button>
                )}
            </div>

            <div className="tc-messages">
                {messagesHasMore[session.id] && (
                    <button className="tc-load-earlier-btn" onClick={() => loadEarlierMessages(session.id)}>
                        ↑ Load earlier messages
                    </button>
                )}
                {sessionMessages.map((msg) => {
                    const isAI = msg.senderType === 'AI';
                    const isOwnMessage = msg.senderId === user?.username;
                    const cls = isAI ? 'ai' : isOwnMessage ? 'mine' : 'other';

                    return (
                        <div key={msg.id} className={`tc-message ${cls}`}>
                            {isAI && (
                                <div className="tc-ai-label">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" fill="none" strokeWidth="2"/>
                                    </svg>
                                    AI Assistant
                                </div>
                            )}
                            {!isAI && !isOwnMessage && (
                                <div className="tc-message-sender">{shortName(msg.senderId)}</div>
                            )}
                            <div className="tc-message-bubble">{msg.content}</div>
                            <div className="tc-message-time">{formatTime(msg.sentAt)}</div>
                        </div>
                    );
                })}

                {showWaiting && (
                    <div className="tc-waiting">
                        <div className="tc-dot-pulse">
                            <span /><span /><span />
                        </div>
                        Waiting for a team member...
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {!isResolved && (
                <div className="tc-thread-input">
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder="Type a message... (Enter to send)"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button className="tc-send-btn" onClick={handleSend} disabled={!input.trim()}>
                        <svg viewBox="0 0 24 24">
                            <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
                        </svg>
                    </button>
                </div>
            )}

            {isResolved && (
                <div style={{ textAlign: 'center', padding: '12px', fontSize: '12px', color: '#888', background: '#f5f5f5' }}>
                    This question has been resolved.
                </div>
            )}
        </div>
    );
}
